// game/shared/src/canyon-placement.ts
//
// Shared, deterministic generator for the stone-canyon framing around the dry
// "Fish" basins (regions.json). Returns the exact mesh placements — perimeter
// walls/cliffs + interior rock clusters — so the CLIENT renders them and the
// SERVER gives them collision + navmesh footprints from the SAME positions.
//
// WHY THIS EXISTS. The canyon framing used to be generated client-side only
// (world/canyon-rocks.ts), so the server never knew the rocks/cliffs existed and
// players walked straight through them. Moving the placement here makes it the
// single source of truth: rendered == collided == pathed, like doodad-placement.
//
// DETERMINISM. Seeded LCG keyed on (region.name + index); no Math.random / Date,
// so the layout is identical across reloads, clients, and the server. The order
// of rng() draws below is load-bearing — changing it reshuffles the whole layout.
// (Terrain height is sampled by each consumer at render/collision time and does
// NOT consume rng, so it stays out of this module.)

// Minimal region shape (subset of regions.json / client RegionRow). Circle uses
// cx/cy/r; everything else is treated as a rect via x1/y1/x2/y2.
export interface CanyonRegion {
  name: string;
  shape: string;
  cx?: number; cy?: number; r?: number;
  x1?: number; y1?: number; x2?: number; y2?: number;
}

export type CanyonPlacement = {
  kind: "wall" | "rock";
  meshKey: string;
  x: number;
  z: number;
  scale: number;
  rotY: number;
};

// ── tuning (mirror of the old world/canyon-rocks.ts knobs) ──
const PERIMETER_DENSITY = 0.04; // wall segments per world-unit of perimeter
const WALL_HEIGHT_MIN = 6;      // metres
const WALL_HEIGHT_MAX = 12;
const CLUSTER_COUNT = 3;        // rock clusters per region
const ROCKS_PER_CLUSTER = 3;
const SCALE_JITTER = 0.25;
const WALL_INSET_MAX = 4;        // world units inward from the rim
const CANYON_WALL_FRACTION = 0.4; // walls vs smaller cliffs

const WALL_KEYS = ["exCanyonW1A", "exCanyonW2A", "exCanyonW3A"] as const;
const CLIFF_KEYS = ["exCliff1A", "exCliff2A", "exCliff5A"] as const;
const ROCK_L_KEYS = ["exRockL1A", "exRockL2A", "exRockL3A"] as const;
const ROCK_M_KEYS = ["exRockM1A", "exRockM2A", "exRockM3A"] as const;

// Native Y-extent (metres) used to scale tall meshes to a target world height.
const NATIVE_HEIGHT: Record<string, number> = {
  exCanyonW1A: 31.5, exCanyonW2A: 31.3, exCanyonW3A: 31.5,
  exCliff1A: 6.2, exCliff2A: 8.6, exCliff5A: 13.4,
  exRockL1A: 3.1, exRockL2A: 3.2, exRockL3A: 3.0,
  exRockM1A: 1.5, exRockM2A: 1.5, exRockM3A: 1.5,
};
const wallScale = (key: string, targetH: number): number => targetH / (NATIVE_HEIGHT[key] ?? 10);

function makeLcg(seed: number): () => number {
  let s = seed >>> 0;
  return (): number => {
    s = (Math.imul(1664525, s) + 1013904223) >>> 0;
    return s / 0xffffffff;
  };
}
function hashStr(str: string): number {
  let h = 5381;
  for (let i = 0; i < str.length; i++) h = (Math.imul(h, 33) ^ str.charCodeAt(i)) >>> 0;
  return h;
}

type Point = { x: number; z: number; tangentAngle: number };
function perimeterPoints(region: CanyonRegion): Point[] {
  if (region.shape === "circle") {
    const cx = region.cx!, cz = region.cy!, r = region.r!;
    const n = Math.max(2, Math.round(2 * Math.PI * r * PERIMETER_DENSITY));
    const step = (2 * Math.PI) / n;
    const pts: Point[] = [];
    for (let i = 0; i < n; i++) {
      const a = i * step;
      pts.push({ x: cx + Math.cos(a) * r, z: cz + Math.sin(a) * r, tangentAngle: a + Math.PI / 2 });
    }
    return pts;
  }
  const x1 = region.x1!, z1 = region.y1!, x2 = region.x2!, z2 = region.y2!;
  if (x2 <= x1 || z2 <= z1) return [];
  const pts: Point[] = [];
  const addSeg = (ax: number, az: number, bx: number, bz: number, tAngle: number): void => {
    const len = Math.hypot(bx - ax, bz - az);
    const n = Math.max(1, Math.round(len * PERIMETER_DENSITY));
    for (let i = 0; i < n; i++) {
      const t = n === 1 ? 0.5 : i / (n - 1);
      pts.push({ x: ax + (bx - ax) * t, z: az + (bz - az) * t, tangentAngle: tAngle });
    }
  };
  addSeg(x1, z1, x2, z1, 0);
  addSeg(x2, z1, x2, z2, Math.PI / 2);
  addSeg(x2, z2, x1, z2, Math.PI);
  addSeg(x1, z2, x1, z1, -Math.PI / 2);
  return pts;
}

function randomInterior(region: CanyonRegion, rng: () => number): { x: number; z: number } {
  if (region.shape === "circle") {
    const ang = rng() * 2 * Math.PI;
    const dist = Math.sqrt(rng()) * region.r! * 0.8;
    return { x: region.cx! + Math.cos(ang) * dist, z: region.cy! + Math.sin(ang) * dist };
  }
  return {
    x: region.x1! + rng() * (region.x2! - region.x1!),
    z: region.y1! + rng() * (region.y2! - region.y1!),
  };
}

/**
 * Generate every canyon placement for the dry "Fish" basins. Pure + deterministic
 * — same regions in, same placements out. Consumers sample terrain height at
 * (x, z) themselves; this returns only the 2-D layout + scale + rotation.
 */
// EXPERIMENT (2026-06-27, feat/pathfinding-navmesh): the dry "Fish" basins were
// never carved into the terrain — they sit at grass elevation (heightmap shows
// basin floor ≈ surrounding ground, 97% walkable). So the perimeter "canyon
// walls" are sheer cliff meshes standing on flat lawn with NO collider (walls are
// non-colliding by design, else they seal the basin), which players walk straight
// through. Rather than carve real crevasses, we're trying REMOVING the canyon
// framing entirely (walls + interior rock clusters) so the basins read as plain
// open ground. Flip back to true to restore the framing.
const CANYON_FRAMING_ENABLED = false;

export function generateCanyonPlacements(regions: CanyonRegion[]): CanyonPlacement[] {
  const out: CanyonPlacement[] = [];
  if (!CANYON_FRAMING_ENABLED) return out;
  if (!regions || regions.length === 0) return out;
  const fish = regions.filter((r) => r.name.toLowerCase().startsWith("fish"));

  for (let ri = 0; ri < fish.length; ri++) {
    const region = fish[ri]!;
    const rng = makeLcg(hashStr(region.name + ri));

    // ── perimeter walls / cliffs ──
    for (const pt of perimeterPoints(region)) {
      const useWall = rng() < CANYON_WALL_FRACTION;
      const pool = useWall ? WALL_KEYS : CLIFF_KEYS;
      const key = pool[Math.floor(rng() * pool.length)]!;
      const inset = rng() * WALL_INSET_MAX;
      let nx = 0, nz = 0;
      if (region.shape === "circle") {
        const dx = pt.x - region.cx!, dz = pt.z - region.cy!;
        const len = Math.hypot(dx, dz) || 1;
        nx = -dx / len; nz = -dz / len;
      } else {
        const cx = (region.x1! + region.x2!) / 2, cz = (region.y1! + region.y2!) / 2;
        const dx = cx - pt.x, dz = cz - pt.z;
        const len = Math.hypot(dx, dz) || 1;
        nx = dx / len; nz = dz / len;
      }
      const px = pt.x + nx * inset, pz = pt.z + nz * inset;
      const targetH = WALL_HEIGHT_MIN + rng() * (WALL_HEIGHT_MAX - WALL_HEIGHT_MIN);
      const s = wallScale(key, targetH) * (1 - SCALE_JITTER / 2 + rng() * SCALE_JITTER);
      const rotY = pt.tangentAngle + (rng() - 0.5) * (Math.PI / 3);
      out.push({ kind: "wall", meshKey: key, x: px, z: pz, scale: s, rotY });
    }

    // ── interior rock clusters ──
    for (let ci = 0; ci < CLUSTER_COUNT; ci++) {
      const center = randomInterior(region, rng);
      for (let k = 0; k < ROCKS_PER_CLUSTER; k++) {
        const spreadRadius = 3 + rng() * 4;
        const ang = rng() * 2 * Math.PI;
        const rx = center.x + Math.cos(ang) * spreadRadius;
        const rz = center.z + Math.sin(ang) * spreadRadius;
        const useLarge = rng() < 0.6;
        const pool2 = useLarge ? ROCK_L_KEYS : ROCK_M_KEYS;
        const rKey = pool2[Math.floor(rng() * pool2.length)]!;
        const nativeH = NATIVE_HEIGHT[rKey] ?? 3;
        const targetRH = nativeH * (0.9 + rng() * 0.4);
        const rs = (targetRH / nativeH) * (1 - SCALE_JITTER / 2 + rng() * SCALE_JITTER);
        const rotY = rng() * Math.PI * 2;
        out.push({ kind: "rock", meshKey: rKey, x: rx, z: rz, scale: rs, rotY });
      }
    }
  }
  return out;
}
